import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Header.css';

const HeaderLoggedIn = ({ user, onLogout }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    // Clear all storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    
    // Call parent logout handler
    if (onLogout) {
      onLogout();
    }
    
    // Force page reload to clear all cached state
    window.location.href = '/';
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown when route changes
  const handleNavigationClick = () => {
    setShowDropdown(false);
  };

  // Calculate cash value from reward points (Universal USD Standard)
  const calculateBalance = () => {
    const points = user?.pointsBalance || 0;
    
    // Universal USD Standard: 100 points = $0.01 USD (10,000 points = $1 USD)
    const amount = points / 10000; // Convert points to USD

    // Display amounts with 2 decimal places for USD (e.g. $0.01)
    const formatted = amount.toFixed(2);

    return {
      points,
      cash: `$${formatted}`,
      symbol: '$',
      amount: formatted
    };
  };

  const balance = calculateBalance();

  // Get country flag emoji based on country code
  const getCountryFlag = (countryCode) => {
    const flags = {
      'AU': '🇦🇺', 'BR': '🇧🇷', 'CA': '🇨🇦', 'CN': '🇨🇳',
      'FR': '🇫🇷', 'DE': '🇩🇪', 'HK': '🇭🇰', 'IN': '🇮🇳',
      'ID': '🇮🇩', 'IT': '🇮🇹', 'JP': '🇯🇵', 'MY': '🇲🇾',
      'MX': '🇲🇽', 'SG': '🇸🇬', 'KR': '🇰🇷', 'ES': '🇪🇸',
      'TH': '🇹🇭', 'AE': '🇦🇪', 'GB': '🇬🇧', 'US': '🇺🇸',
      'VN': '🇻🇳'
    };
    return flags[countryCode] || '🌍';
  };

  const navigationLinks = user?.role === 'admin' 
    ? [
        { path: '/admin', label: 'Admin Dashboard' },
        { path: '/dashboard', label: 'Dashboard' }
      ]
    : [
        { path: '/dashboard', label: 'Dashboard' },
      { path: '/survey-profiler/all', label: 'Survey Profiler' },
        { path: '/projects', label: 'Surveys' },
        { path: '/rewards', label: 'Rewards' }
      ];

  return (
    <header className="home-header custom-header logged-in-header">
      <div className="container">
        <div className="header-content">
          {/* Brand Logo */}
          <div className="navbar-brand">
            <Link to={user?.role === 'admin' ? '/admin' : '/dashboard'} onClick={handleNavigationClick}>
              <img
                alt="Dive-in Panels"
                src="/assets/images/logo.png"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="navbar-nav">
            <ul className="nav-list">
              {navigationLinks.map((link, index) => (
                <li 
                  key={index}
                  className={`nav-item ${window.location.pathname === link.path ? 'active' : ''}`}
                >
                  <Link to={link.path} className="nav-link" onClick={handleNavigationClick}>{link.label}</Link>
                </li>
              ))}
            </ul>

            {/* User Profile Dropdown */}
            <div className="user-profile-section" ref={dropdownRef}>
              <div className="user-info" onClick={toggleDropdown}>
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-details">
                  <span className="user-name">{user?.name || 'User'}</span>
                </div>
                <span className="dropdown-arrow">{showDropdown ? '▲' : '▼'}</span>
              </div>

              {showDropdown && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <p className="dropdown-user-name">{user?.name}</p>
                      <p className="dropdown-user-email">{user?.email}</p>
                      <div className="dropdown-balance">
                        <span className="balance-label">💰 Balance:</span>
                        <span className="balance-points">{balance.points} pts</span>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <ul className="dropdown-menu">
                    <li>
                      <Link to="/current-balance" onClick={() => setShowDropdown(false)}>
                        <span className="dropdown-icon">💰</span> Current Balance
                      </Link>
                    </li>
                    <li>
                      <Link to="/profile" onClick={() => setShowDropdown(false)}>
                        <span className="dropdown-icon">👤</span> User Profile
                      </Link>
                    </li>
                    <li>
                      <Link to="/my-rewards" onClick={() => setShowDropdown(false)}>
                        <span className="dropdown-icon">🎁</span> My Rewards
                      </Link>
                    </li>
                    <li>
                      <Link to="/change-password" onClick={() => setShowDropdown(false)}>
                        <span className="dropdown-icon">🔒</span> Change Password
                      </Link>
                    </li>
                    <li>
                      <button onClick={handleLogout} className="logout-btn">
                        <span className="dropdown-icon">🚪</span> Logout
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu - Simplified for logged-in users */}
          <div className="mobile-user-section">
            <div className="mobile-user-avatar" onClick={toggleDropdown}>
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {showDropdown && (
        <div className="mobile-dropdown">
          <div className="mobile-dropdown-header">
            <p className="mobile-user-name">{user?.name}</p>
            <p className="mobile-user-email">{user?.email}</p>
            <div className="mobile-balance">
              <span>💰 Balance: </span>
              <strong>{balance.cash}</strong>
              <span className="mobile-points"> ({balance.points} pts)</span>
            </div>
          </div>
          <ul className="mobile-dropdown-menu">
            {navigationLinks.map((link, index) => (
              <li key={index}>
                <Link to={link.path} onClick={() => setShowDropdown(false)}>
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="mobile-dropdown-divider"></li>
            <li>
              <Link to="/current-balance" onClick={() => setShowDropdown(false)}>
                💰 Current Balance
              </Link>
            </li>
            <li>
              <Link to="/profile" onClick={() => setShowDropdown(false)}>
                👤 User Profile
              </Link>
            </li>
            <li>
              <Link to="/my-rewards" onClick={() => setShowDropdown(false)}>
                🎁 My Rewards
              </Link>
            </li>
            <li>
              <Link to="/change-password" onClick={() => setShowDropdown(false)}>
                🔒 Change Password
              </Link>
            </li>
            <li>
              <button onClick={handleLogout} className="mobile-logout-btn">
                🚪 Logout
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default HeaderLoggedIn;

